// src/components/BroadcasterCueSequence.js
// D-053: Broadcaster 5-step cue sequence
//
// Replaces the legacy <GoLiveCue> 10-second countdown. Instead of a blind timer,
// the 5 steps gate on ACTUAL pipeline confirmation events so when step 5 fires
// (the big checkmark) the stream is genuinely live on Mux's CDN:
//
//   1. Connecting           "Connecting to Mux…"
//                           — WS is opening, MediaRecorder is starting
//   2. Stabilizing feed     "Stabilizing feed…"
//                           — first ~3s of relay status messages with healthy fps+speed
//   3. Verifying delivery   "Verifying delivery…"
//                           — Mux API poll returned connected=true (upstream ingest)
//   4. Countdown            "Going live in 3…2…1"
//                           — short synchronization window so admin can take a breath
//   5. GO                   "You're live."
//                           — large checkmark, 2s hold, auto-dismiss
//
// Visual language / positioning carries over from GoLiveCue (D-044 pattern #44):
// floating overlay `position: fixed; top: 12; zIndex: 2000`, solid brand-color
// background, white text. Mobile-aware width + max 560px.
//
// The component is externally-driven — LiveBroadcaster passes three gating props:
//   wsOpen         — boolean, true once the WebSocket to the relay is open
//   stabilized     — boolean, true after ~3s of healthy {fps, speed} status frames
//   muxConnected   — boolean, true after /api/live-class?action=status returns connected
// and one callback:
//   onLive         — called when step 5 completes (lets parent stamp `state: 'live'`)
//
// If the component stalls at a step (e.g. muxConnected never arrives) a fallback
// hint appears after 20s so admin knows something's wrong.

import React, { useEffect, useRef, useState } from 'react';

const COUNTDOWN_FROM = 3;         // seconds for step 4
const GO_HOLD_SECONDS = 2;        // seconds to hold step 5 before dismiss
const STALL_HINT_MS = 20000;      // show fallback hint if a step stalls this long

export default function BroadcasterCueSequence({
    active,
    wsOpen,
    stabilized,
    muxConnected,
    onLive,
    onDismiss,
}) {
    // 'connecting' | 'stabilizing' | 'verifying' | 'countdown' | 'go' | 'hidden'
    const [step, setStep] = useState('connecting');
    const [countdown, setCountdown] = useState(COUNTDOWN_FROM);
    const [stalled, setStalled] = useState(false);

    // Keep latest callbacks in refs so we never depend on them in the effects below
    // (inline arrow functions at the call site would otherwise thrash these effects).
    const onLiveRef = useRef(onLive);
    const onDismissRef = useRef(onDismiss);
    useEffect(() => { onLiveRef.current = onLive; }, [onLive]);
    useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);

    // Reset when `active` toggles false (e.g. broadcaster stops).
    useEffect(() => {
        if (!active) {
            setStep('connecting');
            setCountdown(COUNTDOWN_FROM);
            setStalled(false);
        }
    }, [active]);

    // Step 1 → 2: when WS opens, move to stabilizing.
    useEffect(() => {
        if (!active) return;
        if (step === 'connecting' && wsOpen) setStep('stabilizing');
    }, [active, step, wsOpen]);

    // Step 2 → 3: when the relay reports stable {fps, speed}, move to verifying.
    useEffect(() => {
        if (!active) return;
        if (step === 'stabilizing' && stabilized) setStep('verifying');
    }, [active, step, stabilized]);

    // Step 3 → 4: when Mux confirms the ingest is connected, start the final countdown.
    useEffect(() => {
        if (!active) return;
        if (step === 'verifying' && muxConnected) {
            setStep('countdown');
            setCountdown(COUNTDOWN_FROM);
        }
    }, [active, step, muxConnected]);

    // Step 4: countdown 3, 2, 1.
    useEffect(() => {
        if (!active || step !== 'countdown') return;
        if (countdown <= 0) {
            setStep('go');
            onLiveRef.current?.();
            return;
        }
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [active, step, countdown]);

    // Step 5 → hidden: hold the checkmark, then dismiss.
    useEffect(() => {
        if (!active || step !== 'go') return;
        const t = setTimeout(() => {
            setStep('hidden');
            onDismissRef.current?.();
        }, GO_HOLD_SECONDS * 1000);
        return () => clearTimeout(t);
    }, [active, step]);

    // Stall hint — if any gating step stalls for STALL_HINT_MS, show a fallback.
    useEffect(() => {
        if (!active) { setStalled(false); return; }
        if (step === 'go' || step === 'hidden' || step === 'countdown') { setStalled(false); return; }
        const t = setTimeout(() => setStalled(true), STALL_HINT_MS);
        return () => clearTimeout(t);
    }, [active, step]);

    if (!active || step === 'hidden') return null;

    const isGo = step === 'go';

    // Copy table keyed by step
    const copy = {
        connecting: { eyebrow: 'Preparing', body: 'Connecting to Mux…' },
        stabilizing: { eyebrow: 'Preparing', body: 'Stabilizing feed…' },
        verifying: { eyebrow: 'Preparing', body: 'Verifying delivery…' },
        countdown: { eyebrow: 'Going live', body: `Going live in ${countdown > 0 ? countdown : 1}…` },
        go: { eyebrow: "You're live", body: 'Your viewers can see and hear you — go ahead and start the class!' },
    }[step] || { eyebrow: '', body: '' };

    return (
        <div style={{
            position: 'fixed',
            top: 12, left: 12, right: 12,
            margin: '0 auto',
            maxWidth: 560,
            zIndex: 2000,
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 20px', borderRadius: 14,
            background: isGo
                ? 'linear-gradient(135deg, color-mix(in srgb, var(--mom-success, #7a9f7a) 96%, var(--mom-white)), color-mix(in srgb, var(--mom-success, #7a9f7a) 82%, var(--mom-white)))'
                : 'linear-gradient(135deg, color-mix(in srgb, var(--mom-primary) 96%, var(--mom-white)), color-mix(in srgb, var(--mom-primary) 82%, var(--mom-white)))',
            border: `1.5px solid ${isGo ? 'color-mix(in srgb, var(--mom-success, #7a9f7a) 60%, transparent)' : 'color-mix(in srgb, var(--mom-primary) 60%, transparent)'}`,
            boxShadow: `0 8px 32px ${isGo ? 'color-mix(in srgb, var(--mom-success, #7a9f7a) 35%, transparent)' : 'color-mix(in srgb, var(--mom-primary) 35%, transparent)'}, 0 2px 8px rgba(0,0,0,0.08)`,
            backdropFilter: 'blur(8px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(8px) saturate(1.4)',
            transition: 'all 0.4s ease',
            animation: 'goLiveCueSlideIn 0.4s ease-out',
        }}>
            {isGo ? (
                <div style={{
                    flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.95)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--mom-success, #7a9f7a)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                </div>
            ) : step === 'countdown' ? (
                <div style={{
                    flexShrink: 0, width: 44, height: 44, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.95)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                }}>
                    <span style={{
                        fontFamily: 'var(--mom-font-serif)', fontSize: 20, fontWeight: 700,
                        fontStyle: 'italic', color: 'var(--mom-primary)', lineHeight: 1,
                    }}>{Math.max(1, countdown)}</span>
                </div>
            ) : (
                // Pulsing dot — active preparation step
                <div style={{
                    flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.95)',
                        animation: 'pulse 1.3s ease-in-out infinite',
                    }} />
                </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                    fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.85)',
                    margin: 0, marginBottom: 2,
                }}>
                    {copy.eyebrow}
                </p>
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                    color: '#fff', margin: 0, lineHeight: 1.4,
                }}>
                    {copy.body}
                </p>
                {stalled && !isGo && step !== 'countdown' && (
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 500,
                        color: 'rgba(255,255,255,0.88)', marginTop: 6, lineHeight: 1.35,
                    }}>
                        Taking longer than expected — check your internet and keep this tab in focus.
                    </p>
                )}
            </div>
        </div>
    );
}
